import { DisplayKv } from "@/components/display-kv";
import { DomainList, DomainListItemCard } from "@/components/domain";
import { Skeleton } from "@/components/ui/skeleton";
import { useDeleteHotelRoom, useGetHotelRooms } from "@/hooks/hotel-rooms";
import { useGetHotel } from "@/hooks/hotels";
import { T } from "@/i18n";
import { sortDates } from "@/lib/utils";
import { HotelRoom, HotelRoomType } from "@/models";
import { createFileRoute, Link } from "@tanstack/react-router";
import { format } from "date-fns";
import { BedDouble, HotelIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/(hotel-rooms)/hotel-rooms/$id")({
  component: RouteComponent,
});

function RouteComponent() {
  const { id } = Route.useParams();
  const query = useGetHotelRooms(id);
  const { data: hotel, isLoading } = useGetHotel(id);

  return (
    <DomainList
      domain="hotelRooms"
      breadcrumbs={[
        {
          content: (
            <>
              <T tKey="hotels.title" /> <HotelIcon width={16} height={16} />
            </>
          ),
          href: "/hotels",
        },
        {
          content:
            isLoading || !hotel ? (
              <Skeleton className="h-5 w-10" />
            ) : (
              <>
                {hotel.name}
                <HotelIcon width={16} height={16} />
              </>
            ),
          href: "/hotels/$id",
          params: { id },
        },
        {
          content: (
            <>
              <T tKey="hotelRooms.title" /> <BedDouble width={16} height={16} />
            </>
          ),
          href: "/hotel-rooms/$id",
          params: { id },
        },
      ]}
      tableOptions={{
        hasActions: true,
        columns: [
          {
            header: "hotelRooms.fields.designation",
          },
          {
            header: "hotelRooms.fields.notes",
          },
          {
            header: "hotelRooms.fields.count",
          },
          {
            header: "hotelRooms.fields.earlyBookingDiscountDays",
          },
          {
            header: "hotelRooms.fields.extraBeds",
          },
          {
            header: "hotelRooms.fields.averageExtraBedRateMap",
          },
          {
            header: "hotelRooms.fields.pricing",
          },
        ],
      }}
      type="table"
      data={query.data}
      isLoading={query.isLoading}
      newLink="/hotel-rooms/$id/new"
      newLinkParams={{ id }}
      renderItem={(room, index, isTable) => (
        <RoomCard room={room} key={index} hotelId={id} isTable={isTable} />
      )}
    />
  );
}

function RoomCard({
  room,
  hotelId,
  isTable,
}: {
  room: HotelRoom;
  hotelId: string;
  isTable: boolean;
}) {
  const { mutateAsync } = useDeleteHotelRoom(hotelId);
  const deleteRoom = () => mutateAsync(room.id.toString());
  const { t } = useTranslation();
  return (
    <DomainListItemCard
      table={isTable}
      domain="hotelRooms"
      title={room.designation || "-"}
      titleTableOptions={{
        tableClassName: "whitespace-nowrap",
      }}
      description={room.notes}
      descriptionTableOptions={{
        tableClassName: "whitespace-nowrap max-w-44",
        tableZoom: true,
      }}
      editLink={(children, key) => (
        <Link
          key={key}
          to="/hotel-rooms/$id/$rid"
          params={{ id: hotelId, rid: room.id }}
        >
          {children}
        </Link>
      )}
      contentMap={[
        {
          label: t("hotelRooms.fields.count"),
          value: room.count ?? "-",
        },
        {
          label: t("hotelRooms.fields.earlyBookingDiscountDays"),
          value: room.earlyBookingDiscountDays ?? "-",
        },
        {
          label: t("hotelRooms.fields.extraBeds"),
          value: room.extraBeds,
        },
        {
          label: t("hotelRooms.fields.averageExtraBedRateMap"),
          value: (
            <DisplayKv
              list={Object.entries(room?.averageExtraBedRateMap ?? {}).map(
                ([key, value]) => ({
                  label: key,
                  value,
                }),
              )}
            />
          ),
        },
        {
          label: t("hotelRooms.fields.pricing"),
          value: !room.pricing?.length
            ? "-"
            : room.pricing.map(({ type, notes, prices }, i) => (
                <div
                  key={`${type}-${i}`}
                  className="flex w-full flex-col items-start gap-1"
                >
                  <span className="text-foreground">
                    <T tKey={`hotelRooms.types.${type}`} />{" "}
                    {type === HotelRoomType.Other && ``}
                  </span>
                  {type === HotelRoomType.Other && !!notes && (
                    <span className="text-muted-foreground">{notes}</span>
                  )}
                  <DisplayKv
                    list={Object.entries(prices ?? {})
                      .sort(([a], [b]) => sortDates(a, b))
                      .map(([key, value]) => ({
                        label: key
                          .split(" - ")
                          .filter(Boolean)
                          .map((t) => format(new Date(+t), "dd/MM/yyyy"))
                          .join(" - "),
                        value,
                      }))}
                  />
                </div>
              )),
        },
      ]}
      onDelete={deleteRoom}
    />
  );
}
